(() => {
  if (window.AUAboutVideoV4) return;

  const VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;
  const desktop = window.matchMedia('(min-width: 768px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let lastTrigger = null;

  const marketingAllowed = () => {
    try {
      return window.Shopify?.customerPrivacy?.marketingAllowed?.() === true;
    } catch (_error) {
      return false;
    }
  };

  const connectionSavesData = () => Boolean(navigator.connection?.saveData);

  const createIframe = (videoId, title, background = false) => {
    if (!VIDEO_ID_PATTERN.test(videoId)) return null;

    const encodedId = encodeURIComponent(videoId);
    const iframe = document.createElement('iframe');
    const query = background
      ? `autoplay=1&mute=1&loop=1&playlist=${encodedId}&controls=0&playsinline=1&rel=0`
      : 'autoplay=1&playsinline=1&rel=0&cc_load_policy=1&cc_lang_pref=pt';

    iframe.src = `https://www.youtube-nocookie.com/embed/${encodedId}?${query}`;
    iframe.title = title;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;

    if (background) {
      iframe.className = 'av-about__hero-video-frame';
      iframe.tabIndex = -1;
      iframe.setAttribute('aria-hidden', 'true');
    }

    return iframe;
  };

  const removeHeroFrame = (mount) => {
    mount.querySelector('iframe')?.remove();
    mount.closest('.av-about--hero')?.classList.remove('av-about--hero-video-active');
  };

  const syncHeroVideo = () => {
    document.querySelectorAll('[data-av-about-hero-video]').forEach((mount) => {
      const videoId = mount.dataset.youtubeId || '';
      const title = mount.dataset.videoTitle || '';
      const canAutoplay =
        VIDEO_ID_PATTERN.test(videoId) &&
        title !== '' &&
        marketingAllowed() &&
        desktop.matches &&
        !reducedMotion.matches &&
        !connectionSavesData() &&
        mount.dataset.userPaused !== 'true';

      if (!canAutoplay) {
        removeHeroFrame(mount);
        return;
      }

      if (mount.querySelector('iframe')) return;
      const iframe = createIframe(videoId, title, true);
      if (!iframe) return;
      mount.appendChild(iframe);
      mount.closest('.av-about--hero')?.classList.add('av-about--hero-video-active');
    });
  };

  const getDialog = (trigger) =>
    trigger.closest('.av-about--stories')?.querySelector('[data-av-about-video-dialog]') ||
    document.querySelector('[data-av-about-video-dialog]');

  const teardownDialog = (dialog) => {
    dialog.querySelector('[data-av-about-video-mount]')?.replaceChildren();
    dialog.querySelector('[data-av-about-video-transcript]')?.replaceChildren();
    const transcriptPanel = dialog.querySelector('.av-about__video-transcript');
    if (transcriptPanel) {
      transcriptPanel.open = false;
      transcriptPanel.hidden = true;
    }
  };

  const openDialog = (trigger) => {
    const dialog = getDialog(trigger);
    const videoId = trigger.dataset.youtubeId || '';
    const title = trigger.dataset.videoTitle || '';
    const templateId = trigger.dataset.transcriptTemplate || '';
    const transcriptTemplate = templateId ? document.getElementById(templateId) : null;

    if (
      !dialog ||
      typeof dialog.showModal !== 'function' ||
      !VIDEO_ID_PATTERN.test(videoId) ||
      title === ''
    ) {
      return false;
    }

    const mount = dialog.querySelector('[data-av-about-video-mount]');
    const titleNode = dialog.querySelector('[data-av-about-video-title]');
    const transcriptNode = dialog.querySelector('[data-av-about-video-transcript]');
    const transcriptPanel = dialog.querySelector('.av-about__video-transcript');
    const closeButton = dialog.querySelector('[data-av-about-video-close]');
    const transcriptSummary = dialog.querySelector('.av-about__video-transcript summary');
    const iframe = createIframe(videoId, title);
    if (
      !mount ||
      !titleNode ||
      !closeButton?.getAttribute('aria-label')?.trim() ||
      !iframe
    ) {
      return false;
    }

    teardownDialog(dialog);
    document.querySelectorAll('[data-av-about-hero-video]').forEach(removeHeroFrame);
    titleNode.textContent = title;
    mount.appendChild(iframe);
    if (
      transcriptTemplate &&
      transcriptNode &&
      transcriptPanel &&
      transcriptSummary?.textContent?.trim()
    ) {
      transcriptNode.appendChild(transcriptTemplate.content.cloneNode(true));
      transcriptPanel.hidden = false;
    }
    lastTrigger = trigger;
    dialog.showModal();
    closeButton.focus();
    return true;
  };

  const focusableElements = (dialog) =>
    Array.from(
      dialog.querySelectorAll(
        'a[href], button:not([disabled]), summary, iframe, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hidden && element.getClientRects().length > 0);

  document.addEventListener('click', (event) => {
    const heroPause = event.target.closest('[data-av-about-hero-pause]');
    if (heroPause) {
      const hero = heroPause.closest('.av-about--hero');
      const mount = hero?.querySelector('[data-av-about-hero-video]');
      if (mount) {
        mount.dataset.userPaused = 'true';
        removeHeroFrame(mount);
        hero.querySelector('[data-av-about-video-trigger]')?.focus({ preventScroll: true });
      }
      return;
    }

    const trigger = event.target.closest('[data-av-about-video-trigger]');
    if (trigger) {
      if (!marketingAllowed()) return;
      if (openDialog(trigger)) event.preventDefault();
      return;
    }

    const closeButton = event.target.closest('[data-av-about-video-close]');
    if (closeButton) closeButton.closest('dialog')?.close();
  });

  document.addEventListener('keydown', (event) => {
    const dialog = event.target.closest?.('[data-av-about-video-dialog]');
    if (!dialog || !dialog.open || event.key !== 'Tab') return;

    const focusable = focusableElements(dialog);
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('close', (event) => {
    const dialog = event.target.closest?.('[data-av-about-video-dialog]');
    if (!dialog) return;
    teardownDialog(dialog);
    lastTrigger?.focus({ preventScroll: true });
    lastTrigger = null;
    syncHeroVideo();
  }, true);

  document.addEventListener('cancel', (event) => {
    const dialog = event.target.closest?.('[data-av-about-video-dialog]');
    if (dialog) teardownDialog(dialog);
  }, true);

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-av-about-hero-video]').forEach(removeHeroFrame);
    event.target.querySelectorAll?.('[data-av-about-video-dialog]').forEach(teardownDialog);
  });

  document.addEventListener('shopify:section:load', syncHeroVideo);
  const syncConsent = () => {
    if (!marketingAllowed()) {
      document.querySelectorAll('[data-av-about-video-dialog][open]').forEach((dialog) => dialog.close());
    }
    syncHeroVideo();
  };

  document.addEventListener('visitorConsentCollected', syncConsent);
  window.addEventListener('shopify:customer-privacy:consent-changed', syncConsent);
  window.addEventListener('load', syncHeroVideo, { once: true });
  desktop.addEventListener?.('change', syncHeroVideo);
  reducedMotion.addEventListener?.('change', syncHeroVideo);

  window.AUAboutVideoV4 = { sync: syncHeroVideo };
})();
